import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  breakpointAnalysisUrl,
  captableUrl,
  companySummaryUrl,
  financialUrl,
  firmSummaryUrl,
  fundOwnershipUrl,
  fundScheduleOfInvestmentsUrl,
  fundSummaryUrl,
  valuationUrl,
  waterfallUrl,
} from 'common/config/urls';
import { PDF_EXPORT_QUERY_PARAM_EXPORT_MODE } from 'common/constants/pdfExport';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { FIRM_ROUTES } from '../../../pages/Firms/firm.routes';
import { PdfExportConfiguration, PdfExportOption } from '../PdfExportConfigDialog.types';

export interface DynamicUrlParams {
  firmSlug?: string;
  companySlug?: string;
  measurementDate?: string;
  version?: string;
  additionalParams?: Record<string, string>;
}

const getRouteSlugById = (routes: { id: string; slug?: string }[], id: string): string | undefined =>
  routes.find(r => r.id === id)?.slug;

const withQuery = (
  path: string,
  params: { date?: string | null; version?: string | null; exportMode?: string | null }
): string => {
  const url = new URL(path, window.location.origin);
  if (params.date) {
    url.searchParams.set('date', params.date);
  }
  if (params.version) {
    url.searchParams.set('version', params.version);
  }
  if (params.exportMode) {
    url.searchParams.set(PDF_EXPORT_QUERY_PARAM_EXPORT_MODE, params.exportMode);
  }
  return url.toString();
};

const getDefaultUrls = (
  baseUrl: string,
  firmSlug: string | undefined,
  companySlug: string | undefined,
  fundSlug: string | undefined,
  baseQueryString: string,
  currentPath: string
): string[] => {
  if (currentPath.includes('/companies/') && companySlug) {
    const urls = [
      companySummaryUrl,
      financialUrl,
      captableUrl,
      fundOwnershipUrl,
      breakpointAnalysisUrl,
      valuationUrl,
      waterfallUrl,
    ].map(builder => withQuery(builder(firmSlug!, companySlug), { date: undefined, version: undefined, exportMode: 'true' }));
    return urls;
  }

  if (currentPath.includes('/funds/') && fundSlug) {
    return [
      withQuery(fundSummaryUrl(firmSlug!, fundSlug), { exportMode: 'true' }),
      withQuery(fundScheduleOfInvestmentsUrl(firmSlug!, fundSlug), { exportMode: 'true' }),
    ];
  }

  if (currentPath.includes('/firms/') && !currentPath.includes('/companies/') && !currentPath.includes('/funds/')) {
    const summarySlug = getRouteSlugById(FIRM_ROUTES, 'summary');
    if (!summarySlug || !firmSlug) {
      return [];
    }
    // Prefer the builder which handles optional firm summary params
    return [withQuery(firmSummaryUrl(firmSlug), { exportMode: 'true' })];
  }

  return [];
};

/**
 * Hook to build dynamic URLs for PDF export based on current context and selected configuration
 */
export const useDynamicPdfUrls = (customUrls?: string[], configuration?: PdfExportConfiguration): string[] => {
  const location = useLocation();
  const params = useParams();
  const [storeValues] = useStore() as unknown as UseStoreValues;
  const { companyInfo, firmInfo } = storeValues;

  return useMemo(() => {
    // If custom URLs are provided, use them
    if (customUrls && customUrls.length > 0) {
      return customUrls;
    }

    // Build URLs dynamically based on current context and configuration
    const baseUrl = window.location.origin;
    const firmSlug = firmInfo?.slug || params.firmSlugParam;
    const companySlug = companyInfo?.slug || params.companySlugParam;
    const fundSlug = params.fundSlugParam;

    // Extract current query parameters
    const currentSearchParams = new URLSearchParams(location.search);
    const measurementDate = currentSearchParams.get('date');
    const version = currentSearchParams.get('version');

    // Common query params
    const commonQuery = {
      date: measurementDate,
      version,
      exportMode: 'true',
    } as const;

    // If no configuration provided, use default behavior
    if (!configuration) {
      return getDefaultUrls(baseUrl, firmSlug, companySlug, fundSlug, '', location.pathname).map(url =>
        withQuery(url, commonQuery)
      );
    }

    // Build URLs based on selected configuration
    const urls: string[] = [];

    switch (configuration.selectedOption) {
      case PdfExportOption.VALUATIONS:
      case PdfExportOption.OTHER:
        if (companySlug) {
          const { companyOptions } = configuration;
          
          const pushIf = (enabled: boolean, builder: (fs: string, cs: string) => string) => {
            if (!enabled || !firmSlug || !companySlug) {
              return;
            }
            urls.push(withQuery(builder(firmSlug, companySlug), commonQuery));
          };

          pushIf(companyOptions.summary, companySummaryUrl);
          pushIf(companyOptions.financials, financialUrl);
          pushIf(companyOptions.fundOwnership, fundOwnershipUrl);
          pushIf(companyOptions.capTable, captableUrl);
          pushIf(companyOptions.breakpointAnalysis, breakpointAnalysisUrl);
          pushIf(companyOptions.valuations, valuationUrl);
          pushIf(companyOptions.waterfall, waterfallUrl);
        }
        break;

      case PdfExportOption.FUND:
        if (fundSlug) {
          const { fundOptions } = configuration;
          
          if (fundOptions.summary) {
            urls.push(withQuery(fundSummaryUrl(firmSlug!, fundSlug), commonQuery));
          }
          if (fundOptions.scheduleOfInvestments) {
            urls.push(withQuery(fundScheduleOfInvestmentsUrl(firmSlug!, fundSlug), commonQuery));
          }
        }
        break;

      case PdfExportOption.FIRM:
        if (firmSlug) {
          const { firmOptions } = configuration;
          
          if (firmOptions.summary) {
            const summarySlug = getRouteSlugById(FIRM_ROUTES, 'summary');
            if (summarySlug) {
              urls.push(withQuery(firmSummaryUrl(firmSlug!), commonQuery));
            }
          }
        }
        break;

      default:
        // No additional URLs for unknown options
        break;
    }

    return urls;
  }, [customUrls, configuration, location.search, location.pathname, params, companyInfo, firmInfo]);
};
